import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import type { ComponentProps } from 'react'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from '../SearchPlaceSelect/SearchPlaceSelectList'
import ExperiencesSearchPlaceSelectList from './ExperiencesSearchPlaceSelectList'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { rem } from 'polished'
import { take } from '../../../lib/array/arrayUtils'
import Caption from 'LuxKit/Typography/Caption'
import { bumpExperienceRecentSearch, deleteExperienceRecentSearch } from 'components/Experiences/hooks/useExperienceRecentSearches'
import ExperienceRecentSearchSuggestions from './ExperienceRecentSearchSuggestions'

const PlaceControl = styled.div`
  width: 100%;

  &:not(.full-width) {
    position: relative;
  }
`

const SearchMenuListItemContainer = styled.div`
  column-gap: ${rem(40)};

  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
`

const RecentSearchSuggestionsContainer = styled.div`
  margin-bottom:  ${rem(28)};
`

const StyledCaption = styled(Caption)`
  padding: 0;
`

const NUMBER_OF_POPULAR_PLACES_DISPLAYED = 9
const RECENT_SEARCH_SUBTITLE_OPTIONS = {
  includeRooms: false,
}

interface Props {
  /**
   * @default fill-anchor
   */
  size?: ComponentProps<typeof DropdownList>['size']
  className?: string
  'data-testid'?: string
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  value?: App.SearchItem
  required?: boolean
  label: string
  placeholder: string
  localInputRef: React.RefObject<HTMLInputElement>
  searchPhrase: string
  onSearchPhraseChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOpen: (e: React.FocusEvent<HTMLInputElement>) => void
  onClearClick: () => void
  finalOpen: boolean
  closeDropdown: () => void
  showPlaceholder: boolean
  recentSearches: Array<App.FullRecentSearch>
  selectedDropdown: number | undefined
  onRecentSearchDelete: (item: App.FullRecentSearch) => void
  onRecentSearchClick: (item: App.FullRecentSearch) => void
  placeholderTitle?: string
  placeholdersSearches: Array<App.SearchItem>
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
  ) => void
  handleResultsChange: (results: Array<App.SearchItem>, searchPhrase: string, shouldTrack?: boolean, loading?: boolean) => void
  placeTypes?: Array<App.SearchPlaceType>
  searchVertical?: App.SearchPlaceVertical
  overrideSearchItems?: Array<App.SearchItem>
  temporaryTypeaheadId?: string
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  controlsContainerRef?: React.RefObject<HTMLDivElement>
}

function ExperiencesSearchPlaceSelectDropdown(props: Props) {
  const {
    size = 'fill-anchor',
    className,
    onKeyDown,
    value,
    required,
    label,
    placeholder,
    localInputRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    finalOpen,
    closeDropdown,
    showPlaceholder,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    placeholdersSearches,
    onLocationSelect,
    handleResultsChange,
    placeTypes,
    searchVertical,
    overrideSearchItems,
    temporaryTypeaheadId,
    temporarySearchSubmitRef,
    controlsContainerRef,
    'data-testid': dataTestId,
  } = props

  const placeHolderSearchItems = take(placeholdersSearches, NUMBER_OF_POPULAR_PLACES_DISPLAYED)

  const handleOnClick = useCallback((searchItem?: App.SearchItem) => {
    onLocationSelect(searchItem)
  }, [onLocationSelect])

  const handleRecentSearchDelete = useCallback((recentSearch: App.FullRecentSearch) => {
    deleteExperienceRecentSearch(recentSearch)
    onRecentSearchDelete(recentSearch)
  }, [onRecentSearchDelete])

  const handleRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    bumpExperienceRecentSearch(recentSearch)
    onRecentSearchClick(recentSearch)
  }, [onRecentSearchClick])

  return (
    <PlaceControl className={className} onKeyDown={onKeyDown}>
      <HiddenInput
        required={required}
        value={value?.format.mainText}
      />
      <SearchFormTextField
        data-testid={dataTestId}
        label={label}
        placeholder={placeholder}
        ref={localInputRef}
        value={searchPhrase}
        displayValue={value?.format.mainText ?? ''}
        onChange={onSearchPhraseChange}
        onFocus={handleOpen}
        onClear={onClearClick}
      />
      {controlsContainerRef && <DropdownSheet
        size={size}
        anchorRef={controlsContainerRef}
        triggerRef={localInputRef}
        open={finalOpen}
        data-testid="place-select"
        onClose={closeDropdown}
      >
        {showPlaceholder && <>
          {!!recentSearches.length && <RecentSearchSuggestionsContainer>
            <ExperienceRecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={handleRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              iconBackground
              onRecentSearchClick={handleRecentSearchClick}
              horizontal
              subtitleOptions={RECENT_SEARCH_SUBTITLE_OPTIONS}
            />
          </RecentSearchSuggestionsContainer>}
          <VerticalSpacer gap={12}>
            <div>
              {placeholderTitle && <StyledCaption variant="large" weight="bold">{placeholderTitle}</StyledCaption>}
              <SearchMenuListItemContainer className="horizontal">
                {placeHolderSearchItems.map((item, index) => <SearchMenuListItem
                  key={item.value}
                  searchItem={item}
                  onClick={handleOnClick}
                  selected={index + recentSearches.length === selectedDropdown}
                  imageId={item.imageId}
                  searchVertical={searchVertical}
                  useImage
                />)}
              </SearchMenuListItemContainer>
            </div>
          </VerticalSpacer>
        </>}
        {/* SearchPlaceSelect only creates a typeahead id for experience query forms; transfers get the shared destination list. */}
        {!showPlaceholder && temporaryTypeaheadId &&
          <ExperiencesSearchPlaceSelectList
            searchPhrase={searchPhrase}
            typeaheadId={temporaryTypeaheadId}
            selectedDropdown={selectedDropdown}
            onChange={onLocationSelect}
            onResultsChange={handleResultsChange}
            temporarySearchSubmitRef={temporarySearchSubmitRef}
          />}
        {!showPlaceholder && !temporaryTypeaheadId &&
          <SearchPlaceSelectList
            searchPhrase={searchPhrase}
            onResultsChange={handleResultsChange}
            onChange={onLocationSelect}
            placeTypes={placeTypes}
            vertical={searchVertical}
            selectedDropdown={selectedDropdown}
            overrideSearchItems={overrideSearchItems}
            showHorizontalLayout
            useImage
          />}
      </DropdownSheet>}
    </PlaceControl>
  )
}

export default ExperiencesSearchPlaceSelectDropdown
